/**
 * Builds the published trip payload (trip + per-city documents) from Trip and List Builder data.
 */

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import type { Trip, ItineraryDay, ItineraryItem } from '../types/client';
import type {
  PublishedPayload,
  PublishedTripInfo,
  CityPublishedDocument,
  PublishedMeta,
  PublishedCityHistory,
  PublishedActivityItem,
  PublishedMealItem,
  PublishedHotelItem,
  PublishedItineraryBlock,
  PublishedItineraryDay,
} from '../types/publishedTrip';
import type {
  ListBuilderCity,
  ListBuilderActivity,
  ListBuilderMeal,
  ListBuilderHotel,
} from '../types/listBuilder';

dayjs.extend(customParseFormat);

const TIME_FORMATS = ['h:mm A', 'HH:mm', 'H:mm'];

export interface ListBuilderData {
  cities: ListBuilderCity[];
  activities: ListBuilderActivity[];
  meals: ListBuilderMeal[];
  hotels: ListBuilderHotel[];
}

function parseTimeToHHmm(timeStr: string | undefined): string {
  if (!timeStr || !timeStr.trim()) return '09:00';
  const parsed = dayjs(timeStr.trim(), TIME_FORMATS, true);
  return parsed.isValid() ? parsed.format('HH:mm') : '09:00';
}

function addMinutesToTime(timeHHmm: string, minutes: number): string {
  const [h, m] = timeHHmm.split(':').map(Number);
  const total = h * 60 + m + minutes;
  const hh = Math.floor(total / 60) % 24;
  const mm = total % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function mapActivity(a: ListBuilderActivity, confirmationNumber?: string | null): PublishedActivityItem {
  return {
    id: a.id,
    title: a.title,
    description: a.description ?? null,
    operatingHours: a.operatingHours ?? null,
    recommendedTimes: a.recommendedTimes ?? [],
    bestTimeReason: a.bestTimeReason ?? null,
    area: a.area ?? null,
    url: a.url ?? null,
    suggestedDurationMinutes: a.suggestedDurationMinutes,
    transit: a.transit ?? null,
    kidFriendly: a.kidFriendly,
    accessibilityNotes: a.accessibilityNotes ?? null,
    priceCategory: a.priceCategory ?? null,
    tags: a.tags ?? [],
    recommendedIfStayAtLeastDays: a.recommendedIfStayAtLeastDays,
    geocode: a.geocode ?? null,
    address: a.address ?? null,
    phoneNumber: a.phoneNumber ?? null,
    confirmationNumber: confirmationNumber ?? null,
  };
}

function mapMeal(m: ListBuilderMeal, confirmationNumber?: string | null): PublishedMealItem {
  return {
    id: m.id,
    name: m.name,
    cuisine: m.cuisine ?? null,
    description: m.description ?? null,
    operatingHours: m.operatingHours ?? null,
    priceCategory: m.priceCategory ?? null,
    area: m.area ?? null,
    url: m.url ?? null,
    matchScore: m.matchScore,
    reasoning: m.reasoning ?? null,
    reservationRequirement: m.reservationRequirement ?? null,
    kidFriendly: m.kidFriendly,
    tags: m.tags ?? [],
    geocode: m.geocode ?? null,
    address: m.address ?? null,
    phoneNumber: m.phoneNumber ?? null,
    confirmationNumber: confirmationNumber ?? null,
  };
}

function mapHotel(h: ListBuilderHotel, confirmationNumber?: string | null): PublishedHotelItem {
  return {
    id: h.id,
    name: h.name,
    description: h.description ?? null,
    budgetCategory: h.budgetCategory ?? null,
    pricePerNight: h.pricePerNight,
    currency: h.currency ?? null,
    area: h.area ?? null,
    url: h.url ?? null,
    amenities: h.amenities ?? [],
    rating: h.rating,
    familyNotes: h.familyNotes ?? null,
    accessibilityNotes: h.accessibilityNotes ?? null,
    tags: h.tags ?? [],
    geocode: h.geocode ?? null,
    address: h.address ?? null,
    phoneNumber: h.phoneNumber ?? null,
    confirmationNumber: confirmationNumber ?? null,
  };
}

function getMealBlockTypeFromTime(timeStr: string | undefined): 'breakfast' | 'lunch' | 'dinner' {
  const hhmm = parseTimeToHHmm(timeStr);
  const [h] = hhmm.split(':').map(Number);
  if (h < 11) return 'breakfast';
  if (h < 16) return 'lunch';
  return 'dinner';
}

function getItemCityId(
  item: { type: string; activityId?: string; mealId?: string; hotelId?: string },
  activityById: Map<string, ListBuilderActivity>,
  mealById: Map<string, ListBuilderMeal>,
  hotelById: Map<string, ListBuilderHotel>
): string | null {
  if (item.type === 'activity' && item.activityId) {
    const a = activityById.get(item.activityId);
    return a?.cityId ?? null;
  }
  if (item.type === 'meal' && item.mealId) {
    const m = mealById.get(item.mealId);
    return m?.cityId ?? null;
  }
  if (item.type === 'accommodation' && item.hotelId) {
    const h = hotelById.get(item.hotelId);
    return h?.cityId ?? null;
  }
  return null;
}

/** A segment of a calendar day spent in one city */
interface CityDaySegment {
  date: string;
  cityId: string;
  items: ItineraryItem[];
  earliestTime: string;
}

/** City visit = consecutive segments in the same city. Tokyo -> Kyoto -> Tokyo = 3 visits. */
interface CityVisit {
  cityId: string;
  segments: CityDaySegment[];
}

/** Split a single ItineraryDay into segments by city */
function splitDayByCity(
  day: ItineraryDay,
  activityById: Map<string, ListBuilderActivity>,
  mealById: Map<string, ListBuilderMeal>,
  hotelById: Map<string, ListBuilderHotel>
): CityDaySegment[] {
  const itemsByCity = new Map<string, ItineraryItem[]>();

  for (const item of day.items ?? []) {
    const cityId = getItemCityId(item, activityById, mealById, hotelById);
    if (!cityId) continue;
    if (!itemsByCity.has(cityId)) {
      itemsByCity.set(cityId, []);
    }
    itemsByCity.get(cityId)!.push(item);
  }

  const segments: CityDaySegment[] = [];
  itemsByCity.forEach((items, cityId) => {
    const times = items
      .map((it) => parseTimeToHHmm(it.time))
      .sort();
    const earliestTime = times[0] ?? '09:00';
    segments.push({
      date: day.date,
      cityId,
      items,
      earliestTime,
    });
  });

  return segments;
}

function buildCityVisits(
  itineraryDays: ItineraryDay[],
  activityById: Map<string, ListBuilderActivity>,
  mealById: Map<string, ListBuilderMeal>,
  hotelById: Map<string, ListBuilderHotel>
): CityVisit[] {
  const allSegments: CityDaySegment[] = [];

  for (const day of itineraryDays) {
    const daySegments = splitDayByCity(day, activityById, mealById, hotelById);
    allSegments.push(...daySegments);
  }

  allSegments.sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    return a.earliestTime.localeCompare(b.earliestTime);
  });

  const visits: CityVisit[] = [];
  let current: CityVisit | null = null;

  for (const segment of allSegments) {
    if (current && current.cityId === segment.cityId) {
      current.segments.push(segment);
    } else {
      current = { cityId: segment.cityId, segments: [segment] };
      visits.push(current);
    }
  }

  return visits;
}

export function buildPublishedPayload(trip: Trip, listBuilderData: ListBuilderData): PublishedPayload {
  const tripSection: PublishedTripInfo = {
    title: trip.title,
    ...(trip.tripType ? { tripType: trip.tripType } : {}),
    startDate: trip.startDate ?? null,
    endDate: trip.endDate ?? null,
    groupSize: trip.groupSize,
    travelCompanions: trip.travelCompanions ?? [],
    preferences: trip.preferences,
  };

  const activityById = new Map(listBuilderData.activities.map((a) => [a.id, a]));
  const mealById = new Map(listBuilderData.meals.map((m) => [m.id, m]));
  const hotelById = new Map(listBuilderData.hotels.map((h) => [h.id, h]));

  const itineraryDays = trip.itineraryDays ?? [];
  const visits = buildCityVisits(itineraryDays, activityById, mealById, hotelById);

  const cities: CityPublishedDocument[] = visits.map((visit) => {
    const { cityId, segments } = visit;
    const city =
      listBuilderData.cities.find((c) => c.id === cityId) ?? null;
    const cityName = city?.name ?? 'Unknown';
    const country = city?.country ?? '';
    const region = city?.region ?? null;
    const summary =
      trip.cityHistories?.[cityId]?.summary ??
      city?.history?.summary ??
      '';

    const uniqueDates = new Set(segments.map((s) => s.date));
    const stayLengthDays = uniqueDates.size;
    const generatedAt = dayjs().format('YYYY-MM-DD');

    const meta: PublishedMeta = {
      city: cityName,
      region,
      country: country || null,
      timezone: null,
      stayLengthDays,
      generatedAt,
      notes: trip.info || null,
    };

    const cityHistory: PublishedCityHistory = {
      summary,
      keyMilestones: [],
    };

    // Collect confirmation numbers from itinerary items for this city visit
    const activityConfirmations = new Map<string, string>();
    const mealConfirmations = new Map<string, string>();
    const hotelConfirmations = new Map<string, string>();
    for (const segment of segments) {
      for (const item of segment.items) {
        if (item.confirmationNumber) {
          if (item.type === 'activity' && item.activityId) {
            activityConfirmations.set(item.activityId, item.confirmationNumber);
          } else if (item.type === 'meal' && item.mealId) {
            mealConfirmations.set(item.mealId, item.confirmationNumber);
          } else if (item.type === 'accommodation' && item.hotelId) {
            hotelConfirmations.set(item.hotelId, item.confirmationNumber);
          }
        }
      }
    }

    const cityActivities = listBuilderData.activities
      .filter((a) => a.cityId === cityId)
      .sort((a, b) => a.id.localeCompare(b.id));
    const activities: PublishedActivityItem[] = cityActivities.map((a) =>
      mapActivity(a, activityConfirmations.get(a.id))
    );

    const cityMeals = listBuilderData.meals
      .filter((m) => m.cityId === cityId)
      .sort((a, b) => a.id.localeCompare(b.id));
    const lunch: PublishedMealItem[] = cityMeals.map((m) =>
      mapMeal(m, mealConfirmations.get(m.id))
    );
    const breakfast: PublishedMealItem[] = [];
    const dinner: PublishedMealItem[] = [];

    const cityHotels = listBuilderData.hotels
      .filter((h) => h.cityId === cityId)
      .sort((a, b) => a.id.localeCompare(b.id));
    const hotels: PublishedHotelItem[] = cityHotels.map((h) =>
      mapHotel(h, hotelConfirmations.get(h.id))
    );

    const itineraryDaysOut: PublishedItineraryDay[] = segments.map((segment, segmentIndex) => {
      const dateFormatted = dayjs(segment.date).format('MMM D, YYYY');
      const blocks = segment.items
        .map((item) => {
          let startLocalTime: string;
          let endLocalTime: string;
          let type: PublishedItineraryBlock['type'];
          let refId: string;

          if (item.type === 'activity' && item.activityId) {
            startLocalTime = parseTimeToHHmm(item.time);
            const duration = item.durationMinutes ?? 60;
            endLocalTime = addMinutesToTime(startLocalTime, duration);
            type = 'activity';
            refId = item.activityId;
          } else if (item.type === 'meal' && item.mealId) {
            startLocalTime = parseTimeToHHmm(item.time);
            const duration = item.durationMinutes ?? 60;
            endLocalTime = addMinutesToTime(startLocalTime, duration);
            type = getMealBlockTypeFromTime(item.time);
            refId = item.mealId;
          } else if (item.type === 'accommodation' && item.hotelId) {
            startLocalTime = '14:00';
            endLocalTime = '15:00';
            type = 'accommodation';
            refId = item.hotelId;
          } else {
            return null;
          }

          return {
            startLocalTime,
            endLocalTime,
            type,
            refId,
            notes: item.notes ?? null,
          };
        })
        .filter((b): b is NonNullable<typeof b> => b !== null);

      return {
        day: segmentIndex + 1,
        title: `Day ${segmentIndex + 1} - ${dateFormatted}`,
        date: segment.date,
        blocks,
      };
    });

    return {
      meta,
      cityHistory,
      mustSee: [],
      activities,
      breakfast,
      lunch,
      dinner,
      nightWithKids: [],
      nightAdults: [],
      hotels,
      itineraryDays: itineraryDaysOut,
    };
  });

  return {
    trip: tripSection,
    cities,
  };
}
